import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { maturationService } from '../../../services/maturationService';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

const SettingsScreen = ({ guardianProfile, children, onBack, onChildDeleted, onNavigateToSchedule }) => {
  const navigate = useNavigate();
  const [deleteChildDialog, setDeleteChildDialog] = useState(null);
  const [deleteAccountDialog, setDeleteAccountDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);

  const handleExportChild = async (child) => {
    try {
      await maturationService?.exportChildDataCSV(child?.id);
    } catch (err) {
      console.error('Error exporting data:', err);
      setError(err?.message || 'Failed to export data');
    }
  };

  const handleDeleteChild = async (childId) => {
    setIsDeleting(true);
    setError(null);
    try {
      await maturationService?.deleteChild(childId);
      setDeleteChildDialog(null);
      onChildDeleted();
    } catch (err) {
      console.error('Error deleting child:', err);
      setError(err?.message || 'Failed to delete child profile');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      await maturationService?.deleteAccount();
      // Sign out and redirect to landing page
      await supabase?.auth?.signOut();
      navigate('/');
    } catch (err) {
      console.error('Error deleting account:', err);
      setError(err?.message || 'Failed to delete account');
      setIsDeleting(false);
    }
  };

  const handleScheduleClick = () => {
    navigate('/maturation-monitor?view=measurement_schedule');
    window.location?.reload();
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center">
        <Button 
          onClick={onBack}
          variant="ghost"
          size="sm"
          iconName="ArrowLeft"
        >
          Back
        </Button>
        <h1 className="text-3xl font-bold text-text-primary ml-4">Settings</h1>
      </div>

      {error && (
        <div className="bg-error/10 border border-error rounded-lg p-4">
          <div className="flex items-start">
            <Icon name="AlertCircle" className="w-5 h-5 text-error mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-text-primary mb-1">Error</p>
              <p className="text-text-secondary text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-text-primary mb-4">Measurement Schedule</h2>
        <p className="text-text-secondary mb-4">Set measurement frequency preferences and manage email reminders</p>
        <Button
          onClick={onNavigateToSchedule}
          iconName="Calendar"
          variant="outline"
        >
          Manage Schedule & Reminders
        </Button>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-text-primary mb-4">Units Preference</h2>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-text-primary font-medium">Metric Units</p>
            <p className="text-text-secondary text-sm">Height in centimetres, mass in kilograms</p>
          </div>
          <div className="bg-success/10 text-success px-4 py-2 rounded-lg font-medium">
            Active
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-text-primary mb-4">Data Export</h2>
        <p className="text-text-secondary mb-4">Export growth data for each child to CSV format</p>
        <div className="space-y-3">
          {children?.map((child) => (
            <div key={child?.id} className="flex items-center justify-between bg-surface rounded-lg p-4">
              <div className="flex items-center">
                <Icon name="User" className="w-5 h-5 text-primary mr-3" />
                <span className="text-text-primary font-medium">{child?.nickname}</span>
              </div>
              <Button 
                onClick={() => handleExportChild(child)}
                variant="outline"
                size="sm"
                iconName="Download"
              >
                Export CSV
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-text-primary mb-4">Delete Child Profiles</h2>
        <div className="bg-warning/10 border border-warning rounded-lg p-4 mb-4">
          <div className="flex items-start">
            <Icon name="AlertTriangle" className="w-5 h-5 text-warning mr-3 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-text-primary mb-1">Warning</p>
              <p className="text-text-secondary">Deleting a child profile will permanently remove all associated measurements. This action cannot be undone.</p>
            </div>
          </div>
        </div>
        <div className="space-y-3">
          {children?.map((child) => (
            <div key={child?.id} className="flex items-center justify-between bg-surface rounded-lg p-4">
              <div className="flex items-center">
                <Icon name="User" className="w-5 h-5 text-error mr-3" />
                <span className="text-text-primary font-medium">{child?.nickname}</span>
              </div>
              <Button 
                onClick={() => setDeleteChildDialog(child?.id)}
                variant="outline"
                size="sm"
                iconName="Trash2"
              >
                Delete
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-text-primary mb-4">Delete Account</h2>
        <div className="bg-error/10 border border-error rounded-lg p-4 mb-4">
          <div className="flex items-start">
            <Icon name="AlertTriangle" className="w-5 h-5 text-error mr-3 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-text-primary mb-1">Permanent Deletion</p>
              <p className="text-text-secondary">Deleting your account will permanently remove your guardian profile, all child profiles, and all measurements. This action cannot be undone and you will be immediately signed out.</p>
            </div>
          </div>
        </div>
        <Button 
          onClick={() => setDeleteAccountDialog(true)}
          variant="destructive"
          size="md"
          iconName="Trash2"
        >
          Delete Complete Account
        </Button>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-text-primary mb-4">Privacy & Data</h2>
        <div className="space-y-4">
          <div className="flex items-start">
            <Icon name="Lock" className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-text-primary font-medium mb-1">Data Storage</p>
              <p className="text-text-secondary text-sm">Only essential data required for growth tracking is stored. No location data is collected.</p>
            </div>
          </div>
          <div className="flex items-start">
            <Icon name="Shield" className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-text-primary font-medium mb-1">Access Control</p>
              <p className="text-text-secondary text-sm">Only you can view or edit your child's data. No data is shared with third parties.</p>
            </div>
          </div>
          <div className="flex items-start">
            <Icon name="Database" className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-text-primary font-medium mb-1">Data Retention</p>
              <p className="text-text-secondary text-sm">Your data is retained until you choose to delete it. You have full control over your information.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-text-primary mb-4">Safety Caveats</h2>
        <div className="bg-warning/10 border border-warning rounded-lg p-6">
          <div className="flex items-start">
            <Icon name="AlertTriangle" className="w-6 h-6 text-warning mr-3 mt-1 flex-shrink-0" />
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold text-text-primary mb-1">Purpose and Limits</p>
                <p className="text-sm text-text-secondary leading-relaxed">
                  This app is for education and self monitoring. It does not provide medical advice, it does not diagnose conditions, and it cannot predict injuries. If you are worried about your child's health, growth, pain, or wellbeing, contact a qualified healthcare professional.
                </p>
              </div>
              
              <div>
                <p className="text-sm font-semibold text-text-primary mb-1">Uncertainty</p>
                <p className="text-sm text-text-secondary leading-relaxed">
                  Peak height velocity is estimated from patterns in repeated height measurements. Estimates can be wrong, especially when measurements are infrequent or inconsistent. The app will show a confidence grade, and it may refuse to estimate peak height velocity when data quality is insufficient.
                </p>
              </div>
              
              <div>
                <p className="text-sm font-semibold text-text-primary mb-1">Measurement Error</p>
                <p className="text-sm text-text-secondary leading-relaxed">
                  Small errors can create false growth spurts in the data. Measure height in the same way each time, ideally at the same time of day, and use two back to back measures.
                </p>
              </div>
              
              <div>
                <p className="text-sm font-semibold text-text-primary mb-1">Training Advice Boundaries</p>
                <p className="text-sm text-text-secondary leading-relaxed">
                  The guidance provided is general. It is not a personalised training plan. Children develop at different rates, and decisions about sport and exercise should consider enjoyment, readiness, supervision, and the advice of qualified coaches or clinicians.
                </p>
              </div>
              
              <div>
                <p className="text-sm font-semibold text-text-primary mb-1">Safeguarding and Data</p>
                <p className="text-sm text-text-secondary leading-relaxed">
                  This app stores information about a child. Use it only as a parent or guardian, keep login details private, and delete data you no longer need.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-text-primary mb-4">Contact & Support</h2>
        <div className="space-y-3">
          <div className="flex items-start">
            <Icon name="Mail" className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-text-primary font-medium mb-1">Questions or Feedback</p>
              <p className="text-text-secondary text-sm">For questions about the app or to provide feedback, please contact your administrator.</p>
            </div>
          </div>
          <div className="flex items-start">
            <Icon name="FileText" className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-text-primary font-medium mb-1">Consent Status</p>
              <p className="text-text-secondary text-sm">
                Consent accepted on: {guardianProfile?.consentAcceptedAt ? new Date(guardianProfile?.consentAcceptedAt)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Not recorded'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Child Confirmation Dialog */}
      {deleteChildDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-md w-full p-6">
            <div className="flex items-start mb-4">
              <Icon name="AlertTriangle" className="w-6 h-6 text-error mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-2">Delete Child Profile?</h3>
                <p className="text-text-secondary text-sm mb-2">
                  Are you sure you want to delete <span className="font-medium text-text-primary">{children?.find(c => c?.id === deleteChildDialog)?.nickname}</span>?
                </p>
                <p className="text-text-secondary text-sm">
                  This will permanently delete:
                </p>
                <ul className="list-disc list-inside text-text-secondary text-sm mt-2 space-y-1">
                  <li>The child profile</li>
                  <li>All height measurements</li>
                  <li>All body mass measurements</li>
                  <li>All measurement notes</li>
                  <li>All growth data and calculations</li>
                </ul>
                <p className="text-error text-sm font-medium mt-3">
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <Button 
                onClick={() => setDeleteChildDialog(null)}
                variant="outline"
                size="sm"
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button 
                onClick={() => handleDeleteChild(deleteChildDialog)}
                variant="destructive"
                size="sm"
                loading={isDeleting}
              >
                Delete Permanently
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Dialog */}
      {deleteAccountDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-md w-full p-6">
            <div className="flex items-start mb-4">
              <Icon name="AlertTriangle" className="w-6 h-6 text-error mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-2">Delete Complete Account?</h3>
                <p className="text-text-secondary text-sm mb-2">
                  Are you sure you want to delete your entire account?
                </p>
                <p className="text-text-secondary text-sm">
                  This will permanently delete:
                </p>
                <ul className="list-disc list-inside text-text-secondary text-sm mt-2 space-y-1">
                  <li>Your guardian profile</li>
                  <li>All child profiles ({children?.length || 0})</li>
                  <li>All measurements for all children</li>
                  <li>All growth data and calculations</li>
                  <li>Your user account and login credentials</li>
                </ul>
                <p className="text-error text-sm font-medium mt-3">
                  This action cannot be undone and you will be immediately signed out.
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <Button 
                onClick={() => setDeleteAccountDialog(false)}
                variant="outline"
                size="sm"
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleDeleteAccount}
                variant="destructive"
                size="sm"
                loading={isDeleting}
              >
                Delete Everything
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsScreen;